import React, { useEffect, useState } from 'react';

import { ChevronDown, Sun, Moon, Tag, HelpCircle } from 'lucide-react';

import ProfileMenu from './ProfileMenu';
import packageJson from '../../../package.json'; // Adjust the path to your `package.json`
import {
  getUserName,
  getAdminUserName,
  getUserId,
  getRoles,
} from '../../utils/tokenUtils';
import { useProfilePictureSasUrl } from '../../hooks/useUserApi';

const Topbar: React.FC = () => {
  const userId = getUserId();
  const userName = getUserName() || '';
  const adminUsername = getAdminUserName();
  const roles = getRoles();
  const isUser = roles?.includes('User');
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false); // For managing the toggle state

  // Use TanStack Query hook for profile picture (automatically cached)
  const { data: profilePictureUrl } = useProfilePictureSasUrl(userId);

  useEffect(() => {
    // Load dark mode state from localStorage on mount
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleProfileMenu = (): void => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const toggleDarkMode = (): void => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode)); // Save to localStorage
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const getInitials = (name: string): string => {
    const initials = name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();

    return initials || 'U';
  };

  return (
    <header className="flex items-center justify-between h-20 px-4 lg:px-6 bg-white dark:bg-charcoal border-b border-gray-300 dark:border-seam z-[80]">
      <div className="flex items-center space-x-4">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img src="/images/logo.svg" alt="Logo" className="h-12 w-auto dark:hidden" />
          <img src="/images/logo-dark.svg" alt="Logo" className="hidden h-12 w-auto dark:block" />
        </a>

        {/* Version */}
        <div className="hidden lg:flex items-center ml-4 bg-gray-100 dark:bg-seam px-3 py-1.5 rounded-full border border-gray-300 dark:border-seam shadow-sm">
          <Tag className="h-5 w-5 text-gray-500 dark:text-harbor mr-2" />
          <span className="text-sm text-gray-500 dark:text-cream">
            v.{packageJson.version}
          </span>
        </div>
      </div>

      {/* Notifications, Messages, Dark Mode Toggle, and Profile */}
      <div className="flex items-center space-x-4">
        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 dark:bg-seam text-gray-600 dark:text-cream hover:bg-gray-300 dark:hover:bg-seam transition"
          aria-label="Toggle Dark Mode"
        >
          {isDarkMode ? (
            <Sun className="w-5 h-5" aria-hidden="true" />
          ) : (
            <Moon className="w-5 h-5" aria-hidden="true" />
          )}
        </button>

        {/* Help Button - Only for User role */}
        {isUser && (
          <a
            href="https://latreia.co.za/faq/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 dark:bg-seam text-gray-600 dark:text-cream hover:bg-gray-300 dark:hover:bg-seam transition"
            aria-label="Help & FAQ"
          >
            <HelpCircle className="w-5 h-5" aria-hidden="true" />
          </a>
        )}

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-seam cursor-pointer"
            onClick={toggleProfileMenu}
          >
            {/* Profile Picture or Initials */}
            {profilePictureUrl ? (
              <img
                className="rounded-full object-cover"
                style={{ width: '40px', height: '40px' }}
                src={profilePictureUrl}
                alt="Profile"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-500 dark:bg-seam text-white flex items-center justify-center text-sm font-semibold">
                {getInitials(userName)}
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-gray-600 dark:text-cream hidden sm:block">
                {userName}
              </span>
              {adminUsername && (
                <span className="text-gray-500 dark:text-harbor text-xs hidden sm:block">
                  {adminUsername}
                </span>
              )}
            </div>
            <ChevronDown
              className={`w-6 h-6 text-gray-600 dark:text-cream transition-transform ${
                profileMenuOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {profileMenuOpen && <ProfileMenu />}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
